import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableQuery } from '../../../../../gen-src/queries';
import {
  ApiRequestsContext,
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext,
  UIActionsContext,
  VerticalFlexContainer,
} from '../../../data-catalog-shared';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { ColumnDetailsDialogQueryWrapper } from '../ColumnDetails/ColumnDetailsDialog';
import { TableBreadcrumbsUpdater } from '../breadcrumbs/TableBreadcrumbsUpdater';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import {
  getAnchorCountFromConfig,
  LineageItemConfig,
  lineageItemKey,
  LineageSection,
  LineageSectionType,
} from '../placePage/LineageConfiguration';
import { TableLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { ReRenderPlacePageContext } from '../placePage/ReRenderPlacePageContext';
import { memoizedGetTableLineageConfiguration, TableLineagePaneParams } from './TableLineageConfiguration';
import { TableLineageCounts, TableLineageCountsQueryWrapper } from './TableLineageCountsQueryWrapper';
import { TablePlacePageHeader } from './TablePlacePageHeader';
import { TableQueryWrapper } from './TableQueryWrapper';
import { TableSubplaceParamsProvider } from './TableSubplaceParamsProvider';


export interface TablePlacePageProps {
  readonly tableId: string;
  readonly hideLaunchEditOnMobile?: boolean;
  readonly newFlowFromCatalog?: boolean;
  readonly projectIdForNewDocuments?: string;
}

export interface TablePlacePageState {
  readonly hackyUpdateNumber: number; // Increment number to rerender
}

export class TablePlacePage extends React.Component<TablePlacePageProps, TablePlacePageState> {
  static override contextType = UIActionsContext
  override context!: React.ContextType<typeof UIActionsContext>

  private localIsMounted = false;

  public constructor(props: TablePlacePageProps) {
    super(props);
    this.state = {
      hackyUpdateNumber: 0
    };
  }

  public override componentDidMount() {
    this.localIsMounted = true;
    this.context.maybeSendDataQualitySubscriptionEvent(this.props.tableId, 'table');
  }

  public override componentWillUnmount() {
    this.localIsMounted = false;
    this.context.maybeSendDataQualitySubscriptionEvent(this.props.tableId, 'table');
  }

  private rerenderTablePlacePage = () => {
    if (this.localIsMounted) {
      this.setState({
        hackyUpdateNumber: this.state.hackyUpdateNumber + 1
      });
    }
  }

  public override render(): JSX.Element {
    return (
      <ReRenderPlacePageContext.Provider value={{ rerender: this.rerenderTablePlacePage }}>
        <SiteUrlsContext.Consumer>
          {urls =>
            <FeatureFlagsContext.Consumer>
              {
                featureFlags =>
                  <Switch>
                    {memoizedGetTableLineageConfiguration(
                      featureFlags.metricsDataCatalogEnabled,
                      featureFlags.publishedConnectionsLineageEnabled,
                      featureFlags.lensLineageEnabled).map(lineageSection =>
                        lineageSection.items.map((lineageItem, i) =>
                          <Route
                            key={i}
                            path={lineageItem.path(urls, this.props.tableId)}
                            render={() => this.renderRoute(urls, lineageItem, lineageSection)}
                          />
                        )
                      )}
                    <Route
                      render={({ location }) =>
                        <Switch>
                          <Redirect
                            exact={true}
                            from={urls.catalogTablePath(this.props.tableId)}
                            to={{ ...location, pathname: urls.catalogTableColumnsPath(this.props.tableId) }}
                          />
                          <UnrecognizedUrl
                            backToButtonLabel={Messages.BackToTable()}
                            backToButtonUrl={urls.catalogTablePath(this.props.tableId)}
                          />
                        </Switch>
                      }
                    />
                  </Switch>
              }
            </FeatureFlagsContext.Consumer>
          }
        </SiteUrlsContext.Consumer>
      </ReRenderPlacePageContext.Provider>
    );
  }

  private readonly renderRoute = (
    urls: SiteUrls,
    lineageItem: LineageItemConfig<TableLineageCounts, TableLineagePaneParams>,
    lineageSection: LineageSection<TableLineageCounts, TableLineagePaneParams>,
  ): JSX.Element => {
    return (
      <TableSubplaceParamsProvider>
        {params =>
          <ErrorListProvider>
            <ErrorToasts />
            <TableQueryWrapper tableId={this.props.tableId}>
              {table =>
                <TableLineageCountsQueryWrapper
                  tableId={this.props.tableId}
                  selectedColumnKeys={params.selectedColumns}
                >
                  {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                    <>
                      <TableBreadcrumbsUpdater
                        tableId={this.props.tableId}
                        tableName={table.name}
                        databaseId={table.__typename === 'DatabaseTable' && table.database ? table.database.id : null}
                        databaseName={table.__typename === 'DatabaseTable' && table.database ? table.database.name : null}
                      />
                      <BrowserTitleUpdater
                        name={table.name}
                        browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_PLACE_ENTITY_NAME}
                      />
                      <VerticalFlexContainer fillFlexParent={true}>
                        <ApiRequestsContext.Consumer>
                          {apiRequests =>
                            <TablePlacePageHeader
                              apiRequests={apiRequests}
                              hideLaunchEditOnMobile={this.props.hideLaunchEditOnMobile}
                              newFlowFromCatalog={this.props.newFlowFromCatalog}
                              projectIdForNewDocuments={this.props.projectIdForNewDocuments}
                              table={table}
                            />
                          }
                        </ApiRequestsContext.Consumer>
                        <TableLineageSubplace
                          tableId={this.props.tableId}
                          table={table}
                          lineageItem={lineageItem}
                          lineageSection={lineageSection}
                          countsLoading={countsLoading}
                          unfilteredCounts={unfilteredCounts}
                          filteredCounts={filteredCounts}
                          urlSearchValue={params.urlSearchValue}
                          selectedKeys={params.selectedColumns}
                          updateSelectionForKey={params.updateColumnSelection}
                          clearColumnSelection={params.clearColumnSelection}
                          hackyUpdateNumber={this.state.hackyUpdateNumber}
                        />
                      </VerticalFlexContainer>
                      {lineageSection.sectionType === LineageSectionType.Anchor ?
                        <Route
                          path={urls.catalogTableColumnPath(this.props.tableId, ':columnId')}
                          render={({ history, location, match }: RouteComponentProps<{ columnId: string }>) =>
                            <ColumnDetailsDialogQueryWrapper
                              columnId={match.params.columnId}
                              tableVizportalId={table.__typename === 'DatabaseTable' ? table.vizportalId : null}
                              onClose={() => history.replace({
                                ...location,
                                pathname: urls.catalogTableColumnsPath(this.props.tableId)
                              })}
                              afterDescriptionAndTagsChange={this.rerenderTablePlacePage}
                            />
                          }
                        />
                        : null
                      }
                    </>
                  }
                </TableLineageCountsQueryWrapper>
              }
            </TableQueryWrapper>
          </ErrorListProvider>
        }
      </TableSubplaceParamsProvider>
    );
  }

}

type TableLineageSubplaceProps = {
  readonly tableId: string;
  readonly table: Unpacked<DatabaseTableQuery['databaseTables']>;
  readonly lineageItem: LineageItemConfig<TableLineageCounts, TableLineagePaneParams>;
  readonly lineageSection: LineageSection<TableLineageCounts, TableLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: TableLineageCounts | null;
  readonly filteredCounts: TableLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearColumnSelection: () => void;
  readonly hackyUpdateNumber: number;
} & SelectionProviderChildProps;

class TableLineageSubplace extends React.PureComponent<TableLineageSubplaceProps> {

  public override render(): JSX.Element {
    const selectedColumns = Object.keys(this.props.selectedKeys);
    const hasFilter = selectedColumns.length > 0;
    const filteredColumnId: string | null = hasFilter ? selectedColumns[0] : null;
    const lineageCounts = this.props.filteredCounts ? this.props.filteredCounts : this.props.unfilteredCounts;
    return (
      <FeatureFlagsContext.Consumer>
        {
          featureFlags =>
            <TableLineageSubplaceLayout
              activeLineageItemKey={lineageItemKey(this.props)}
              selectedAnchorItemIds={selectedColumns}
              pane={this.props.lineageItem.paneFactory({
                title: messageWithOrWithoutCount(
                  lineageCounts && this.props.lineageItem.count(lineageCounts),
                  this.props.lineageItem.messagesForPane
                ),
                table: this.props.table,
                tableId: this.props.tableId,
                filteredColumnId,
                count: lineageCounts && this.props.lineageItem.count(lineageCounts),
                selectedKeys: this.props.selectedKeys,
                updateSelectionForKey: this.props.updateSelectionForKey,
                clearSelection: this.props.clearColumnSelection,
                hackyUpdateNumber: this.props.hackyUpdateNumber
              })}
              tableId={this.props.tableId}
              countsLoading={this.props.countsLoading}
              unfilteredCounts={this.props.unfilteredCounts}
              filteredCounts={this.props.filteredCounts}
              urlSearchValue={this.props.urlSearchValue}
              clearColumnSelection={this.props.clearColumnSelection}
              anchorName={this.props.table.name}
              anchorCount={getAnchorCountFromConfig(
                memoizedGetTableLineageConfiguration(
                  featureFlags.metricsDataCatalogEnabled,
                  featureFlags.publishedConnectionsLineageEnabled,
                  featureFlags.lensLineageEnabled),
                this.props.unfilteredCounts)}
              filteredOnItemType={hasFilter ? LineageItemType.Table : null}
              filteredItemName={this.props.filteredCounts?.filteredColumnName ?? null}
            />
        }
      </FeatureFlagsContext.Consumer>
    );
  }

}
